/**
 * Mutações do roadmap: um hook por operação, todos invalidando o agregado.
 * O toggle de conclusão e o de item de checklist são otimistas — a UI muda na
 * hora e desfaz se o servidor recusar.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/cliente';
import type { Roadmap, Tarefa } from '../api/tipos';

/** Exportado porque a barra lateral consulta o mesmo roadmap, pela mesma chave. */
export const SLUG = 'mistik-island';
export const CHAVE_ROADMAP = ['roadmap', SLUG] as const;

export interface ItemChecklist {
  id: number;
  texto: string;
  feito: boolean;
  responsavelId: number | null;
  ordem: number;
}

export interface Checklist {
  tarefaId: number;
  itens: ItemChecklist[];
}

export const chaveChecklist = (tarefaId: number) => ['checklist', tarefaId] as const;

export function useMutacoesRoadmap() {
  const fila = useQueryClient();
  const invalidar = () => {
    void fila.invalidateQueries({ queryKey: CHAVE_ROADMAP });
    // A bolinha junto: marcar a si mesmo como responsável gera notificação, e a
    // contagem só recarrega sozinha a cada 30s. O aviso chegava — depois de meio
    // minuto olhando um avatar sem bolinha, que é indistinguível de não ter chegado.
    void fila.invalidateQueries({ queryKey: ['notificacoes'] });
  };

  const criarTarefa = useMutation({
    mutationFn: (corpo: {
      nome: string;
      grupoId: number;
      mesInicio: number;
      mesFim: number;
      responsaveis?: number[];
    }) =>
      api<Tarefa>(`/api/projetos/${SLUG}/tarefas`, {
        method: 'POST',
        body: JSON.stringify(corpo),
      }),
    onSuccess: invalidar,
  });

  const editarTarefa = useMutation({
    mutationFn: ({
      id,
      ...corpo
    }: {
      id: number;
      nome?: string;
      descricao?: string;
      grupoId?: number;
      mesInicio?: number;
      mesFim?: number;
      concluida?: boolean;
      responsaveis?: number[];
    }) => api<Tarefa>(`/api/tarefas/${id}`, { method: 'PATCH', body: JSON.stringify(corpo) }),
    // Otimista para o toggle de conclusão: o checkbox responde na hora.
    onMutate: async (mudanca) => {
      await fila.cancelQueries({ queryKey: CHAVE_ROADMAP });
      const anterior = fila.getQueryData<Roadmap>(CHAVE_ROADMAP);
      if (anterior && mudanca.concluida !== undefined) {
        fila.setQueryData<Roadmap>(CHAVE_ROADMAP, {
          ...anterior,
          tarefas: anterior.tarefas.map((t) =>
            t.id === mudanca.id ? { ...t, concluida: mudanca.concluida! } : t
          ),
        });
      }
      return { anterior };
    },
    onError: (_erro, _mudanca, contexto) => {
      if (contexto?.anterior) fila.setQueryData(CHAVE_ROADMAP, contexto.anterior);
    },
    onSettled: invalidar,
  });

  const excluirTarefa = useMutation({
    mutationFn: (id: number) => api<void>(`/api/tarefas/${id}`, { method: 'DELETE' }),
    onSuccess: invalidar,
  });

  const moverTarefa = useMutation({
    mutationFn: ({
      id,
      grupoId,
      aposTarefaId,
    }: {
      id: number;
      grupoId: number;
      aposTarefaId: number | null;
    }) =>
      api<void>(`/api/tarefas/${id}/posicao`, {
        method: 'PATCH',
        body: JSON.stringify({ grupoId, aposTarefaId }),
      }),
    onSuccess: invalidar,
  });

  return { criarTarefa, editarTarefa, excluirTarefa, moverTarefa };
}

export function useMutacoesChecklist(tarefaId: number) {
  const fila = useQueryClient();
  const invalidar = () => {
    void fila.invalidateQueries({ queryKey: chaveChecklist(tarefaId) });
    // O badge n/total muda junto — nos dois lugares onde uma tarefa aparece. A chave do
    // quadro é prefixo, então isto pega o Dev Board sem precisar saber o slug.
    void fila.invalidateQueries({ queryKey: CHAVE_ROADMAP });
    void fila.invalidateQueries({ queryKey: ['quadro'] });
    // E a bolinha: pôr alguém como responsável por um item avisa essa pessoa, que pode
    // ser quem está clicando.
    void fila.invalidateQueries({ queryKey: ['notificacoes'] });
  };

  const criarItem = useMutation({
    mutationFn: (texto: string) =>
      api<ItemChecklist>(`/api/tarefas/${tarefaId}/checklist`, {
        method: 'POST',
        body: JSON.stringify({ texto }),
      }),
    onSuccess: invalidar,
  });

  const editarItem = useMutation({
    mutationFn: ({
      id,
      ...corpo
    }: {
      id: number;
      texto?: string;
      feito?: boolean;
      responsavelId?: number;
      removerResponsavel?: boolean;
    }) =>
      api<ItemChecklist>(`/api/checklist/${id}`, { method: 'PATCH', body: JSON.stringify(corpo) }),
    // Otimista para o checkbox do item.
    onMutate: async (mudanca) => {
      await fila.cancelQueries({ queryKey: chaveChecklist(tarefaId) });
      const anterior = fila.getQueryData<Checklist>(chaveChecklist(tarefaId));
      if (anterior && mudanca.feito !== undefined) {
        fila.setQueryData<Checklist>(chaveChecklist(tarefaId), {
          ...anterior,
          itens: anterior.itens.map((i) =>
            i.id === mudanca.id ? { ...i, feito: mudanca.feito! } : i
          ),
        });
      }
      return { anterior };
    },
    onError: (_erro, _mudanca, contexto) => {
      if (contexto?.anterior) fila.setQueryData(chaveChecklist(tarefaId), contexto.anterior);
    },
    onSettled: invalidar,
  });

  const excluirItem = useMutation({
    mutationFn: (id: number) => api<void>(`/api/checklist/${id}`, { method: 'DELETE' }),
    onSuccess: invalidar,
  });

  return { criarItem, editarItem, excluirItem };
}
